/**
 * Vimeo Player Utility
 * Provides utilities for playing Vimeo videos using the Vimeo Player API.
 * Uses global state on window to support multiple webpack bundles.
 */

const VIMEO_API_URL = 'https://player.vimeo.com/api/player.js';
const VIMEO_API_SCRIPT_ID = 'vimeo-player-api';
const TIMEOUT_MS = 10000;

// Use global state to share between bundles
const GLOBAL_STATE_KEY = '__papaLpVimeoAPI';

/**
 * Get or initialize global API state
 */
function getGlobalState() {
  if (!window[GLOBAL_STATE_KEY]) {
    window[GLOBAL_STATE_KEY] = {
      ready: false,
      loading: false,
      error: null,
      pendingCallbacks: [],
      players: new Map(),
    };
  }
  return window[GLOBAL_STATE_KEY];
}

/**
 * Load Vimeo Player API script
 * @returns {Promise<void>}
 */
function loadVimeoAPI() {
  return new Promise((resolve, reject) => {
    const state = getGlobalState();

    // Already ready (check window.Vimeo as primary indicator)
    if (window.Vimeo?.Player) {
      state.ready = true;
      state.loading = false;
      return resolve();
    }
    
    // Previous load failed
    if (state.error) {
      return reject(state.error);
    }
    
    // Currently loading - queue callback
    if (state.loading) {
      return state.pendingCallbacks.push({ resolve, reject });
    }
    
    // Check for existing script (might be loaded by another bundle)
    if (document.getElementById(VIMEO_API_SCRIPT_ID)) {
      state.loading = true;
      state.pendingCallbacks.push({ resolve, reject });
      return waitForAPI();
    }
    
    // Start loading
    state.loading = true;
    state.pendingCallbacks.push({ resolve, reject });
    
    // Inject script
    const script = document.createElement('script');
    script.id = VIMEO_API_SCRIPT_ID;
    script.src = VIMEO_API_URL;
    script.async = true;
    script.onload = () => {
      const s = getGlobalState();
      s.ready = true;
      s.loading = false;
      flushCallbacks();
    };
    script.onerror = () => handleAPIError(new Error('Failed to load Vimeo Player API'));
    
    document.head.appendChild(script);
    
    // Timeout fallback
    setTimeout(() => {
      const s = getGlobalState();
      if (!s.ready && !window.Vimeo?.Player) {
        handleAPIError(new Error('Vimeo API load timeout'));
      }
    }, TIMEOUT_MS);
  });
}

/**
 * Wait for API to be ready (when script already exists)
 */
function waitForAPI() {
  const startTime = Date.now();
  const checkInterval = setInterval(() => {
    const state = getGlobalState();
    // Check window.Vimeo as primary indicator
    if (window.Vimeo?.Player) {
      clearInterval(checkInterval);
      state.ready = true;
      state.loading = false;
      flushCallbacks();
    } else if (Date.now() - startTime > TIMEOUT_MS) {
      clearInterval(checkInterval);
      handleAPIError(new Error('Vimeo API ready timeout'));
    }
  }, 100);
}

/**
 * Handle API load error
 */
function handleAPIError(error) {
  const state = getGlobalState();
  state.loading = false;
  state.error = error;
  state.pendingCallbacks.forEach(({ reject }) => reject(error));
  state.pendingCallbacks.length = 0;
}

/**
 * Flush all pending callbacks
 */
function flushCallbacks() {
  const state = getGlobalState();
  state.pendingCallbacks.forEach(({ resolve }) => resolve());
  state.pendingCallbacks.length = 0;
}

/**
 * Create control methods for a player
 */
function createPlayerControls(player, playerId, videoId) {
  let isCurrentlyPlaying = false;

  player.on('play', () => { isCurrentlyPlaying = true; });
  player.on('pause', () => { isCurrentlyPlaying = false; });
  player.on('ended', () => { isCurrentlyPlaying = false; });

  return {
    player,
    playerId,
    videoId,
    provider: 'vimeo',
    play: () => player.play().catch(() => {}),
    pause: () => player.pause().catch(() => {}),
    mute: () => player.setMuted(true).catch(() => {}),
    unmute: () => player.setMuted(false).catch(() => {}),
    toggleMute: async () => {
      try {
        const muted = await player.getMuted();
        return player.setMuted(!muted);
      } catch {
        return Promise.resolve();
      }
    },
    isMuted: async () => {
      try {
        return await player.getMuted();
      } catch {
        return true;
      }
    },
    isPlaying: () => isCurrentlyPlaying,
    getState: () => isCurrentlyPlaying ? 1 : 2, // 1 = playing, 2 = paused (similar to YT states)
    destroy: () => {
      try {
        player.destroy();
      } catch (error) {
        console.error('Vimeo player destroy error:', error);
      }
      getGlobalState().players.delete(playerId);
    }
  };
}

/**
 * Get container element
 */
function getContainer(container) {
  if (typeof container === 'string') {
    return document.getElementById(container) || document.querySelector(container);
  }
  return container;
}

/**
 * Create a Vimeo player instance
 * @param {string|HTMLElement} container - Container element or selector
 * @param {string} videoId - Vimeo video ID
 * @param {object} options - Player options
 * @returns {Promise<object>} Player controls
 */
export async function createVimeoPlayer(container, videoId, options = {}) {
  await loadVimeoAPI();
  
  if (!videoId) {
    throw new Error('Invalid Vimeo video ID');
  }
  
  const containerElement = getContainer(container);
  if (!containerElement) {
    throw new Error('Container element not found');
  }
  
  // Generate unique player ID
  const playerId = `vimeo-player-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  containerElement.id = containerElement.id || playerId;
  
  // Build player configuration
  const config = {
    width: '100%',
    height: '100%',
    autoplay: options.autoplay || false,
    muted: options.mute || false,
    loop: options.loop || false,
    controls: options.controls !== false,
    responsive: true,
  };

  // For unlisted videos, use full URL instead of id + h
  // This is required by Vimeo's API for private/unlisted videos
  if (options.hash) {
    config.url = `https://player.vimeo.com/video/${videoId}?h=${options.hash}`;
  } else {
    config.id = videoId;
  }
  
  // Create player
  const player = new window.Vimeo.Player(containerElement, config);
  
  // Wait for player to be ready
  await player.ready();
  
  // Store and return controls
  const state = getGlobalState();
  const controls = createPlayerControls(player, playerId, videoId);
  state.players.set(playerId, controls);
  return controls;
}

/**
 * Get a player instance by ID
 * @param {string} playerId - Player ID
 * @returns {object|null} Player controls or null
 */
export function getPlayer(playerId) {
  const state = getGlobalState();
  return state.players.get(playerId) || null;
}

/**
 * Check if Vimeo API is ready
 * @returns {boolean}
 */
export function isAPIReady() {
  return window.Vimeo?.Player !== undefined;
}

/**
 * Destroy all players
 */
export function destroyAllPlayers() {
  const state = getGlobalState();
  state.players.forEach((controls, id) => {
    try {
      controls.destroy();
    } catch (error) {
      console.error(`Error destroying player ${id}:`, error);
    }
  });
  state.players.clear();
}
